import React, {createContext, useCallback, useContext} from 'react';
import {type FCWithChildren} from 'types';
import type {Media} from 'services/media/types';
import {useGetMediaByEansQuery} from 'services/media/mediaApi';
import {isMockWorkId, useGetWorkQuery} from 'services/metadata';
import {useLoadError} from 'services/utils';
import {useMockContext} from 'services/utils/contexts';
import {queryStatusToLoadStatus} from 'store/api';
import {SceneLoader} from 'components/scene/SceneLoader';

type Props = {
  workId: string;
};

type WorkAssetsContextType = {
  getMediaByEan: (ean: string | undefined) => Media[];
};

export const WorkAssetsContext = createContext<WorkAssetsContextType>({
  // Might not have context, because ManifestationPreview is rendered from rightPanel.
  // In these instances we dont want or need to fetch media.
  getMediaByEan: (_ean: string | undefined) => [],
});

export const WorkAssetsContextProvider: FCWithChildren<Props> = ({
  workId,
  children,
}) => {
  const mock = useMockContext() || isMockWorkId(workId);

  const {data: workMetadataData} = useGetWorkQuery(
    {id: workId},
    {
      skip: mock,
    },
  );

  const eans =
    ((workMetadataData?.manifestations ?? [])
      .map(m => m.ean)
      .filter(e => e) as string[]) ?? [];

  // Preload all at once, will be used my each manifestation later.
  const {
    data: mediasForEans,
    status: mediaStatus,
    isLoading: mediaIsLoading,
  } = useGetMediaByEansQuery(
    {workId, eans},
    {
      skip: mock || eans.length === 0,
    },
  );

  const getMediaByEan = useCallback(
    (ean: string | undefined): Media[] => {
      if (!ean || !mediasForEans) {
        return [];
      }
      const mediaForEan = mediasForEans.find(m => m.ean === ean);
      return mediaForEan ? mediaForEan.medias : [];
    },
    [mediasForEans],
  );

  useLoadError(
    queryStatusToLoadStatus(mediaStatus),
    'page.metadata.media.load.failed',
    'warning',
  );

  if (mediaIsLoading) {
    return <SceneLoader loadingType={'media'} applySceneHeight />;
  }

  return (
    <WorkAssetsContext.Provider value={{getMediaByEan}}>
      {children}
    </WorkAssetsContext.Provider>
  );
};

export const useWorkAssetsContext = (): WorkAssetsContextType => {
  return useContext(WorkAssetsContext);
};
